import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Save, Loader2 } from "lucide-react";
import { FormSection } from "@/components/service-form/FormSection";
import { BasicInformation } from "@/components/service-form/BasicInformation";
import { PricingDuration } from "@/components/service-form/PricingDuration";
import { DescriptionField } from "@/components/service-form/DescriptionField";
import { ServiceImagesUpload } from "@/components/service-form/ServiceImagesUpload";
import { ServiceModeSetting } from "@/components/service-form/ServiceModeSetting";
import { useServiceForm } from "@/hooks/useServiceForm";
import { useServiceFormSubmit } from "@/components/service-form/ServiceFormSubmit";
import { Service } from "@/types/dashboard";
import { Switch } from "@/components/ui/switch";
import { useEffect, useRef, useState } from "react";
import { getInitialDuration } from "@/utils/durationUtils";
import { getSavedLocation } from "@/utils/locationUtils";
import { useToast } from "@/hooks/use-toast";
import { BookingSettings } from "./BookingSettings";
import { useTranslation } from "react-i18next";

interface ServiceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  initialData?: Service | null;
  refreshServices?: () => Promise<void>;
  isLoading?: boolean;
}

export const ServiceFormDialog = ({ 
  open, 
  onOpenChange,
  onSuccess, 
  initialData, 
  refreshServices,
  isLoading = false
}: ServiceFormDialogProps) => {
  const { t } = useTranslation();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const formInitializedRef = useRef(false);
  const categoriesLoadedRef = useRef(false);
  
  const {
    loading: formLoading,
    categories,
    images,
    setImages,
    coverImageIndex,
    setCoverImageIndex,
    selectedCategoryName,
    formData,
    updateFormField,
    handleLocationChange,
    userProfession,
    resetForm,
    fetchCategories,
    setFormData,
    setCategories,
    formInitialized
  } = useServiceForm(initialData);

  // Track if initialData changes between dialog opens
  const [previousInitialDataId, setPreviousInitialDataId] = useState<string | undefined>(undefined);
  const [deletedImageIds, setDeletedImageIds] = useState<string[]>([]);

  const { toast } = useToast();

  // Effect to handle categories loading
  useEffect(() => {
    if (open && !categoriesLoadedRef.current) {
      console.log("Fetching categories");
      fetchCategories().then(() => {
        categoriesLoadedRef.current = true;
      });
    }
  }, [open, fetchCategories]);

  // Reset deleted images when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Clear deletedImageIds when dialog closes
      setDeletedImageIds([]);
    }
  }, [open]);

  // Effect to handle form initialization
  useEffect(() => {
    if (open) {
      console.log("Dialog opened, checking initialization state");
      setInitialLoadComplete(false);
      
      const currentInitialDataId = initialData?.id;
      const initialDataChanged = currentInitialDataId !== previousInitialDataId;
      
      if (initialDataChanged) {
        console.log("Initial data changed, resetting form state");
        setPreviousInitialDataId(currentInitialDataId);
        formInitializedRef.current = false;
        categoriesLoadedRef.current = false;
        setDeletedImageIds([]); // Reset deleted images on initial data change
      }
      
      if (!initialData) {
        console.log("No initial data, setting up new form");
        resetForm();
        setHasInitialized(true);
        setInitialLoadComplete(true);
      }
    }

    if (!open) {
      setHasInitialized(false);
      formInitializedRef.current = false;
      categoriesLoadedRef.current = false;
      setInitialLoadComplete(false);
    }
  }, [open, initialData, resetForm, previousInitialDataId]);

  // Effect to handle form data initialization after categories are loaded
  useEffect(() => {
    if (open && initialData && !hasInitialized && categoriesLoadedRef.current) {
      console.log("Initializing form data with:", initialData);
      setFormData({
        name: initialData.name || "",
        category: initialData.category_id || "",
        price: initialData.price ? initialData.price.toString() : "",
        discountedPrice: initialData.discounted_price ? initialData.discounted_price.toString() : "",
        duration: initialData.duration ? getInitialDuration(initialData.duration) : "1-hour",
        customDuration: initialData.duration && ![30, 60, 120].includes(initialData.duration) 
          ? initialData.duration.toString() 
          : "",
        description: initialData.description || "",
        instantBooking: initialData.instant_booking ?? false,
        requiresScheduling: initialData.requires_scheduling ?? true,
        serviceMode: initialData.service_mode || "my-location",
        travelFee: initialData.travel_fee ? initialData.travel_fee.toString() : "",
        serviceArea: initialData.service_area || getSavedLocation(),
      });
      formInitializedRef.current = true;
      setHasInitialized(true);
    }
  }, [open, initialData, hasInitialized, setFormData, categoriesLoadedRef.current]);

  // Track when all data has finished loading
  useEffect(() => {
    if (open && (
      (!initialData && hasInitialized) || 
      (initialData && hasInitialized && formInitialized)
    )) {
      // Only set initialLoadComplete to true when the form is fully initialized
      console.log("Form is now fully initialized and ready to display");
      setTimeout(() => {
        setInitialLoadComplete(true);
      }, 300); // Small delay to ensure UI has all the data
    }
  }, [open, hasInitialized, initialData, formInitialized]);

  const { submitService, isSubmitting, uploadProgress } = useServiceFormSubmit(async () => {
    try {
      console.log("Service submitted successfully, calling onSuccess callback");
      setSubmitInProgress(true);
      
      // Ensure services are refreshed immediately after successful submission
      if (refreshServices) {
        console.log("Refreshing services after successful submission");
        await refreshServices();
      }
      
      // Call edge function to update completeness after service create/update
      try {
        const { getAuthToken } = await import("@/lib/auth");
        const { updateProfileCompleteness } = await import("@/hooks/useProfileSettings");
        const accessToken = await getAuthToken();
        // Try to get userId from localStorage or session (or pass as prop if available)
        const userId = localStorage.getItem('supabase.auth.token')
          ? JSON.parse(localStorage.getItem('supabase.auth.token') || '{}')?.currentSession?.user?.id
          : undefined;
        if (userId && accessToken) {
          updateProfileCompleteness(userId, accessToken);
        }
      } catch (e) {
        console.error("Failed to call updateProfileCompleteness after service form submit:", e);
      }
      
      // Close the dialog after successful submission - this will trigger onOpenChange
      onOpenChange(false);
      
      // Allow a small delay before calling onSuccess to avoid race conditions
      setTimeout(async () => {
        if (onSuccess) {
          console.log("Calling onSuccess callback");
          await onSuccess();
        }
        setSubmitInProgress(false);
      }, 300);
    } catch (error) {
      console.error("Error in submitService success callback:", error);
      setSubmitInProgress(false);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInProgress) {
      console.log("Submit already in progress, ignoring duplicate submit");
      return;
    }
    
    console.log("Form submitted, validating data");
    
    // Validate that discounted price is not higher than regular price
    if (formData.discountedPrice && formData.price) {
      const regularPrice = parseFloat(formData.price);
      const discountedPrice = parseFloat(formData.discountedPrice);
      
      if (!isNaN(regularPrice) && !isNaN(discountedPrice) && discountedPrice >= regularPrice) {
        toast({
          title: "Validation Error",
          description: "Discounted price must be less than the regular price.",
          variant: "destructive",
        });
        return;
      }
    }
    
    setSubmitInProgress(true);

    let serviceDuration = 60; // default 1 hour
    let durationUnit = "minutes";

    if (formData.duration === "30-min") {
      serviceDuration = 30;
    } else if (formData.duration === "1-hour") {
      serviceDuration = 60;
    } else if (formData.duration === "2-hours") {
      serviceDuration = 120;
    } else if (formData.duration === "custom" && formData.customDuration) {
      serviceDuration = parseInt(formData.customDuration, 10);
    }

    try {
      console.log("Deleted image IDs:", deletedImageIds);
      
      await submitService({
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price) || 0,
        discountedPrice: formData.discountedPrice,
        categoryId: formData.category || null,
        serviceDuration: serviceDuration,
        durationUnit: durationUnit,
        serviceMode: formData.serviceMode,
        serviceLocation: formData.serviceArea,
        instantBooking: formData.instantBooking,
        requiresScheduling: formData.requiresScheduling,
        images: images,
        coverImageIndex: coverImageIndex,
        initialData: initialData,
        travelFee: formData.travelFee,
        deletedImageIds: deletedImageIds
      });
      // Success is handled in the callback passed to useServiceFormSubmit
    } catch (error) {
      console.error("Error submitting service form:", error);
      setSubmitInProgress(false);
    }
  };

  const handleCancel = () => {
    console.log("Cancel button clicked, will call onOpenChange");
    onOpenChange(false);
  };

  // Show loading state if form is still loading data
  const loading = formLoading || isLoading || !initialLoadComplete;

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-screen overflow-y-auto">
          <DialogTitle>{t('services.loadingService')}</DialogTitle>
          <DialogDescription>{t('services.loadingServiceData')}</DialogDescription>
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-gebeya-pink" />
            <p className="mt-2 text-gray-600">{t('services.loadingServiceDataMessage')}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(newOpenState) => {
      // Only allow closing the dialog if no submission is in progress
      if (submitInProgress && newOpenState === false) {
        console.log("Cannot close dialog while submission is in progress");
        return;
      }
      onOpenChange(newOpenState);
    }}>
      <DialogContent className="max-w-3xl max-h-screen overflow-y-auto">
        <DialogTitle>{initialData ? t('services.editService') : t('services.createService')}</DialogTitle>
        <DialogDescription>
          {initialData 
            ? t('services.updateExistingService')
            : t('services.addNewServiceToPortfolio')}
        </DialogDescription>
        
        <div className="max-w-3xl mx-auto">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}>
            <div className="flex justify-end gap-3 mb-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="w-auto"
                size="sm"
                disabled={submitInProgress}
              >
                <X className="mr-1 h-4 w-4" />
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                className="w-auto bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:opacity-90 transition-opacity"
                disabled={isSubmitting || submitInProgress}
                size="sm"
              >
                {isSubmitting || submitInProgress ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    {initialData ? t('services.saving') : t('services.creating')}
                  </>
                ) : (
                  <>
                    <Save className="mr-1 h-4 w-4" />
                    {initialData ? t('services.saveChanges') : t('services.createService')}
                  </>
                )}
              </Button>
            </div>

            <FormSection number="1" title={t('services.basicInformation')}>
              <BasicInformation
                name={formData.name}
                setName={(value) => updateFormField('name', value)}
                category={formData.category}
                setCategory={(value) => updateFormField('category', value)}
                categories={categories}
                setCategories={setCategories}
                profession={userProfession}
              />
            </FormSection>

            <FormSection number="2" title={t('services.pricingDuration')}>
              <PricingDuration
                price={formData.price}
                setPrice={(value) => updateFormField('price', value)}
                discountedPrice={formData.discountedPrice}
                setDiscountedPrice={(value) => updateFormField('discountedPrice', value)}
                duration={formData.duration}
                setDuration={(value) => updateFormField('duration', value)}
                customDuration={formData.customDuration}
                setCustomDuration={(value) => updateFormField('customDuration', value)}
              />
            </FormSection>

            <FormSection number="3" title={t('services.serviceDescriptionMedia')}>
              <div className="space-y-4">
                <DescriptionField
                  description={formData.description}
                  setDescription={(value) => updateFormField('description', value)}
                  serviceName={formData.name}
                  categoryName={selectedCategoryName}
                />

                <ServiceImagesUpload
                  images={images}
                  setImages={setImages}
                  coverImageIndex={coverImageIndex}
                  setCoverImageIndex={setCoverImageIndex}
                  serviceName={formData.name}
                  uploadProgress={uploadProgress}
                  isSubmitting={isSubmitting || submitInProgress}
                  deletedImageIds={deletedImageIds}
                  setDeletedImageIds={setDeletedImageIds}
                  serviceCategory={selectedCategoryName}
                  description={formData.description}
                />
              </div>
            </FormSection>

            <FormSection number="4" title={t('services.serviceModeLocation')}>
              <ServiceModeSetting
                serviceMode={formData.serviceMode}
                setServiceMode={(value) => updateFormField('serviceMode', value)}
                serviceArea={formData.serviceArea}
                onLocationChange={handleLocationChange}
                travelFee={formData.travelFee}
                setTravelFee={(value) => updateFormField('travelFee', value)}
                requiresScheduling={formData.requiresScheduling}
              />
            </FormSection>

            <FormSection number="5" title={t('services.bookingAvailability')}>
              <BookingSettings
                instantBooking={formData.instantBooking}
                setInstantBooking={(value) => updateFormField('instantBooking', value)}
                requiresScheduling={formData.requiresScheduling}
                setRequiresScheduling={(value) => updateFormField('requiresScheduling', value)}
              />
            </FormSection>

            {/* Bottom action buttons */}
            <div className="flex justify-end gap-3 mt-10 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="w-auto"
                size="sm"
                disabled={submitInProgress}
              >
                <X className="mr-1 h-4 w-4" />
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                className="w-auto bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:opacity-90 transition-opacity"
                disabled={isSubmitting || submitInProgress}
                size="sm"
              >
                {isSubmitting || submitInProgress ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    {initialData ? t('services.saving') : t('services.creating')}
                  </>
                ) : (
                  <>
                    <Save className="mr-1 h-4 w-4" />
                    {initialData ? t('services.saveChanges') : t('services.createService')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
